import { Box, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import femaleAudioFile from '../../../assets/voice_demos/female_voice_demo.mp3';
import maleAudioFile from '../../../assets/voice_demos/male_voice_demo.mp3';
import { useAgentRoles } from '../../../contexes/AgentRolesProvider';
import AuthContext from '../../../contexes/AuthContext';
import DashboardContext from '../../../contexes/DashboardContext';
import { useSubscription } from '../../../contexes/SubscriptionProvider';
import AgentCreationStep from '../../../enums/AgentCreationStep';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import { submitCreateAgent } from '../../../helpers/agentFormCreateSubmit';
import { submitUpdateAgent } from '../../../helpers/agentFormUpdateSubmit';
import {
  firstIncompleteStep,
  isStepComplete,
} from '../../../helpers/agentFormStepCompletion';
import {
  getDefaultAgentRoleId,
  usedCustomPhoneNumbers as getUsedCustomPhoneNumbers,
  usedSystemPhoneNumbers as getUsedSystemPhoneNumbers,
} from '../../../helpers/agentHelper';
import { generateUniqueKey } from '../../../helpers/app';
import {
  notifyAgentCreated,
  notifyEmbedClose,
} from '../../../helpers/embedMessaging';
import { shouldShowUpgradeCta } from '../../../helpers/billingPlanSwitchUi';
import {
  parseDigests,
  parseExcludeUrlPatterns,
  parseWebscrapLinks,
  parseWebscrapSourcePage,
  WEBSCRAP_DEFAULT_MAX_PAGES,
  WEBSCRAP_DEFAULT_PAGES_PER_PDF,
} from '../../../helpers/knowledgeHelper';
import { shouldBlockNewCallAgent } from '../../../helpers/pricingAgentLimits';
import { useCompanyAgents } from '../../../hooks/queries/useCompanyAgents';
import useAgentFormSessionRecovery from '../../../hooks/useAgentFormSessionRecovery';
import { useAnalytics } from '../../../hooks/useAnalytics';
import useCompany from '../../../hooks/useCompany';
import useEmbedMode from '../../../hooks/useEmbedMode';
import { useIsFeatureEnabled } from '../../../hooks/useIsFeatureEnabled';
import useKnowledgeFile from '../../../hooks/useKnowledgeFile';
import { useSnackBar } from '../../../hooks/useSnackBar';
import useStepNavigation from '../../../hooks/useStepNavigation';
import useStepValidationGate from '../../../hooks/useStepValidationGate';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  Agent,
  AgentFormData,
  AgentLanguage,
  AgentStatus,
  DepartmentContact,
} from '../../../interfaces/Agent';
import { ResponseObject, WorkflowType } from '../../../interfaces/App';
import { FeatureGate } from '../../../interfaces/FeatureGate';
import Knowledge from '../../../interfaces/Knowledge';
import { DEFAULT_RESOURCE_ACCESS } from '../../../interfaces/ResourceAccess';
import { getCompanyAgents } from '../../../services/agentService';
import { setCalendarAuthState } from '../../../services/calendarService';
import { hasPartnerSession } from '../../../services/partnerSession';
import {
  buildKnowledgeFileCacheKey,
  indexDBService,
} from '../../../services/indexDBService';
import StyledForm from '../../../styles/StyledForm';
import StyledFormStep from '../../../styles/StyledFormStep';
import { canPerformAction, PERMISSIONS } from '../../../utils/permissions';
import {
  canManageAgentAccess,
  normalizeResourceAccess,
} from '../../../utils/resourceAccess';
import AgentAnalyticsSettingsStep from '../../forms/AgentAnalyticsSettingsStep/AgentAnalyticsSettingsStep';
import AgentBehaviorStep from '../../forms/AgentBehaviorStep/AgentBehaviorStep';
import AgentCapabilitiesStep from '../../forms/AgentCapabilitiesStep/AgentCapabilitiesStep';
import AgentKnowledgeStep from '../../forms/AgentKnowledgeStep/AgentKnowledgeStep';
import AgentLineStep from '../../forms/AgentLineStep/AgentLineStep';
import AgentCreationProcessingScreen from '../../shared/AgentCreationProcessingScreen/AgentCreationProcessingScreen';
import AgentCreationSuccessActions from '../../shared/AgentCreationSuccessActions/AgentCreationSuccessActions';
import AgentProfileAvatar from '../../shared/AgentProfileAvatar/AgentProfileAvatar';
import BillingSupportLinks from '../../shared/BillingSupportLinks/BillingSupportLinks';
import FreePlanLimitModal from '../../shared/FreePlanLimitModal/FreePlanLimitModal';
import Modal from '../../shared/Modal/Modal';
import Text from '../../shared/Text/Text';
import WorkflowFooter from '../../shared/WorkflowFooter/WorkflowFooter';
import WorkflowSidebar from '../../shared/WorkflowSidebar/WorkflowSidebar';
import AgentCreationSuccessContainer from './AgentCreationSuccessContainer';

const clearSessionStorage = () => {
  // Clear session storage
  sessionStorage.removeItem('elora_agent_form_state');
  sessionStorage.removeItem('placeholderAgentId');
  sessionStorage.removeItem('calendar_oauth_in_progress');
};

interface AgentFormProps {
  agent?: Agent;
  knowledge?: Knowledge;
  workflow?: WorkflowType;
  step?: number; // Add this prop
}

const AgentForm = ({
  agent,
  knowledge,
  workflow = WorkflowType.CREATE,
  step, // Add this parameter
}: AgentFormProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEmbed = useEmbedMode();
  const isPartnerIframe = hasPartnerSession();
  const lineCompletenessOptions = {
    skipPhoneLineRequirement: isPartnerIframe,
  };
  const agentCreatedNotified = useRef(false);
  const [searchParams] = useSearchParams();

  // Generate and store placeholder agent ID for creation flow
  const [placeholderAgentId, setPlaceholderAgentId] = useState<string>('');

  const { roles: agentRoles } = useAgentRoles();
  const defaultAgentRoleId = getDefaultAgentRoleId(agentRoles);

  // Restore placeholder agent ID from session storage first, then generate if needed
  useEffect(() => {
    if (workflow === WorkflowType.CREATE) {
      try {
        // First, try to restore from session storage
        const stored = sessionStorage.getItem('placeholderAgentId');
        if (stored) {
          setPlaceholderAgentId(stored);
          return; // Exit early if we found a stored ID
        }

        // Only generate new UUID if no stored ID exists
        if (!placeholderAgentId) {
          const uuid = generateUniqueKey();
          setPlaceholderAgentId(uuid);

          // Store in session storage for persistence across page reloads
          try {
            sessionStorage.setItem('placeholderAgentId', uuid);
          } catch (error) {
            console.warn(
              'Failed to save placeholder agent ID to sessionStorage:',
              error
            );
          }
        }
      } catch (error) {
        console.warn(
          'Failed to read placeholder agent ID from sessionStorage:',
          error
        );

        // Fallback: generate new UUID if sessionStorage fails
        if (!placeholderAgentId) {
          const uuid = generateUniqueKey();
          setPlaceholderAgentId(uuid);
        }
      }
    }
  }, [workflow, placeholderAgentId]);

  // Clear calendar OAuth flag when component mounts (user returning from OAuth)
  useEffect(() => {
    if (sessionStorage.getItem('calendar_oauth_in_progress')) {
      sessionStorage.removeItem('calendar_oauth_in_progress');
    }
  }, []);

  // Map currentStep index to AgentCreationStep enum
  const stepEnumOrder = [
    AgentCreationStep.BEHAVIOR,
    AgentCreationStep.KNOWLEDGE,
    AgentCreationStep.CAPABILITIES,
    AgentCreationStep.ANALYTICS,
    AgentCreationStep.LINE,
  ];

  // Step navigation tracking (both workflows — previously EDIT-only; see
  // docs/specs/agent-form-click-to-jump-navigation.md "Decisions Locked In")
  const {
    currentStep,
    currentStepEnum,
    goToStep,
    goToPreviousStep,
    setStepSilently,
  } = useStepNavigation<AgentCreationStep>({
    stepEnumOrder,
    onStepChange: ({ fromStep, toStep, direction, timeSpentMs }) => {
      // Track time spent on previous step
      if (timeSpentMs !== undefined) {
        track(AnalyticsEvents.AGENT_EDIT_STEP_DURATION, {
          step: fromStep,
          timeSpentMs,
        });
      }
      // Track navigation
      track(AnalyticsEvents.AGENT_EDIT_STEP_NAVIGATION, {
        fromStep,
        toStep,
        direction,
      });
    },
  });

  // Handle step parameter from URL or prop for both create and edit workflows
  useEffect(() => {
    if (
      workflow === WorkflowType.CREATE ||
      (workflow === WorkflowType.EDIT && agent)
    ) {
      const stepParam = searchParams.get('step');
      const stepToUse =
        step || (stepParam ? parseInt(stepParam, 10) : undefined);

      if (stepToUse !== undefined) {
        const stepIndex = stepToUse;
        if (stepIndex >= 0 && stepIndex < stepEnumOrder.length) {
          setStepSilently(stepIndex);
        }
      }
    }
  }, [workflow, searchParams, stepEnumOrder.length, agent, step]);

  const { currentUser, setCurrentUser } = useContext(AuthContext);
  const { agentsCount, setAgentsCount } = useContext(DashboardContext);
  const { subscription } = useSubscription();
  const { companyDetails } = useCompany(currentUser);

  // Fetched directly (rather than relying solely on DashboardContext, which is
  // only populated as a side effect of the agents list table mounting) so the
  // free-plan agent limit can be enforced from the very first step, even on a
  // fresh visit to this page.
  const { data: existingAgents, isLoading: isExistingAgentsLoading } =
    useCompanyAgents(workflow === WorkflowType.CREATE);
  const existingAgentsCount = existingAgents?.length ?? agentsCount;
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false); // reused for updating
  const [isKnowledgeCreated, setIsKnowledgeCreated] = useState(false); // reused for updating
  const [isAgentCreated, setIsAgentCreated] = useState(false); // reused for updating
  const [isFilesUploaded, setIsFilesUploaded] = useState(false); // reused for updating

  // Track whether the session completed successfully so the cleanup effect
  // can distinguish an abandoned session from a completed one.
  const sessionCompletedRef = useRef(false);
  useEffect(() => {
    if (isAgentCreated) sessionCompletedRef.current = true;
  }, [isAgentCreated]);

  // Clear both stores on unmount if the session was abandoned (browser back,
  // navbar navigation, or any other path that bypasses navigateToAgents).
  useEffect(() => {
    return () => {
      if (!sessionCompletedRef.current) {
        clearSessionStorage();
        indexDBService
          .clearKnowledgeFiles(
            buildKnowledgeFileCacheKey('call-agent', knowledge?.id)
          )
          .catch(() => {});
      }
    };
  }, []);

  const [newAgent, setNewAgent] = useState<Agent>();

  const [usedCustomPhoneNumbers, setUsedCustomPhoneNumbers] = useState<
    (string | undefined)[]
  >([]);

  const displayMessage = useSnackBar();

  const { track } = useAnalytics();

  const isBookingCapabilityEnabled = useIsFeatureEnabled(
    FeatureGate.BOOKING_CAPABILITY
  );
  const canEditAgentAccess = canManageAgentAccess(currentUser, agent);

  const methods = useForm<AgentFormData>({
    defaultValues: {
      agent_name: '',
      insert_appointment: [],
      company: companyDetails?.name,
      custom_phone_number: '',
      default_language: AgentLanguage.ENGLISH,
      default_sentence: '',
      data_to_collect: [],
      external_capabilities: '',
      knowledge_ids: [],
      knowledge_name: '',
      knowledge_language: 'en',
      existing_knowledge_id: '',
      webscrap_enabled: false,
      webscrap_frequency: 'weekly',
      webscrap_source_url: '',
      webscrap_use_sitemap: true,
      webscrap_max_depth: 1,
      webscrap_max_pages: WEBSCRAP_DEFAULT_MAX_PAGES,
      webscrap_pages_per_pdf: WEBSCRAP_DEFAULT_PAGES_PER_PDF,
      webscrap_links: [] as { url: string; depth?: number }[],
      webscrap_exclude_url_patterns: [] as { value: string }[],
      webscrap_scrape_images: false,
      webscrap_digests: [],
      opening_sentence: '',
      role_id: defaultAgentRoleId || '',
      status: AgentStatus.IN_REVIEW,
      system_phone_number: '',
      system_phone_number_country_code: '',
      failure_backup_phone_number: '',
      time_zone: '',
      working_hours: undefined,
      holidays: [],
      transfer_calls: [],
      employee_directory_staged_file: null,
      voice: 'female',
      analytics_language: 'english',
      call_categories: [{ name: '', description: '', email: '' }],
      auto_fix: false,
      unknown_category_email: '',
      access: { ...DEFAULT_RESOURCE_ACCESS },
    },
  });

  // Set default role when roles are loaded (only for CREATE workflow)
  useEffect(() => {
    if (
      workflow === WorkflowType.CREATE &&
      agentRoles.length > 0 &&
      !methods.getValues('role_id')
    ) {
      const receptionistRole = agentRoles.find(
        agentRole => agentRole?.name.toLowerCase() === 'receptionist'
      );
      if (receptionistRole) {
        methods.setValue('role_id', receptionistRole.id);
      }
    }
  }, [agentRoles, workflow, methods]);

  // Set default unknown category email from company details (only for CREATE workflow)
  useEffect(() => {
    if (
      workflow === WorkflowType.CREATE &&
      companyDetails?.email &&
      !methods.getValues('unknown_category_email')
    ) {
      methods.setValue('unknown_category_email', companyDetails.email);
    }
  }, [companyDetails, workflow, methods]);

  // Track if the form is dirty
  const [isFormDirty, setIsFormDirty] = useState(false);
  // Monitor the form state
  useEffect(() => {
    setIsFormDirty(methods.formState.isDirty);
  }, [methods.formState.isDirty]);

  const {
    isKnowledgeFilesLoading,
    knowledgeFiles,
    knowledgeFilesData,
    pendingDeletions,
    executeStagedDeletions,
    markKnowledgeFileForDeletion,
    unmarkKnowledgeFileForDeletion,
    handleDownloadKnowledgeFile,
    setIsKnowledgeFilesLoading,
    setKnowledgeFilesData,
    clearIndexDBFiles,
    clearKnowledgeFilesData,
  } = useKnowledgeFile(knowledge as Knowledge, 'call-agent');

  const [isAgentCreationCompleted, setIsAgentCreationCompleted] =
    useState(false);

  // Persists/restores Behavior, Knowledge, and Capabilities-step fields to
  // sessionStorage so a calendar OAuth redirect doesn't lose in-progress
  // form data — see docs/specs/agent-form-click-to-jump-navigation.md.
  const { persistStepDataToSessionStorage } = useAgentFormSessionRecovery({
    methods,
    currentStep,
    workflow,
    isBookingCapabilityEnabled,
  });

  useEffect(() => {
    if (workflow === WorkflowType.EDIT && agent) {
      // Check for session storage data to preserve (from OAuth flow)
      let sessionInsertAppointment: any = [];
      let sessionExternalCapabilities = '';

      if (isBookingCapabilityEnabled) {
        const savedState = sessionStorage.getItem('elora_agent_form_state');
        if (savedState) {
          try {
            const parsedData = JSON.parse(savedState);
            sessionInsertAppointment = parsedData.insert_appointment || [];
            sessionExternalCapabilities =
              parsedData.external_capabilities || '';
          } catch (error) {
            console.warn('Failed to parse session storage for EDIT:', error);
          }
        }
      }

      // Determine the initial external_capabilities value based on existing agent data or session storage
      let initialExternalCapabilities: string | string[];

      if (isBookingCapabilityEnabled) {
        // Prefer session storage data if available, otherwise use agent data
        if (sessionExternalCapabilities) {
          initialExternalCapabilities = sessionExternalCapabilities;
        } else if (
          Array.isArray(agent?.external_capabilities?.transfer_calls) &&
          (agent?.external_capabilities?.transfer_calls as DepartmentContact[])
            .length > 0
        ) {
          initialExternalCapabilities = 'transfer_calls';
        } else if (
          agent?.external_capabilities?.insert_appointment &&
          agent.external_capabilities.insert_appointment.length > 0
        ) {
          initialExternalCapabilities = 'insert_appointment';
        } else {
          initialExternalCapabilities = '';
        }
      } else {
        // For the old flow, set to 'transfer_calls' if transfer_calls exist, otherwise empty string
        initialExternalCapabilities =
          Array.isArray(agent?.external_capabilities?.transfer_calls) &&
          (agent?.external_capabilities?.transfer_calls as DepartmentContact[])
            .length > 0
            ? 'transfer_calls'
            : '';
      }

      const webscrapSource = parseWebscrapSourcePage(
        knowledge?.webscrap?.source_page,
        knowledge?.webscrap?.max_depth
      );

      methods.reset({
        agent_name: agent?.name,
        insert_appointment:
          sessionInsertAppointment.length > 0
            ? sessionInsertAppointment
            : agent?.external_capabilities?.insert_appointment || [],
        company: agent?.company,
        default_language: agent?.default_language,
        default_sentence: agent?.default_sentence,
        data_to_collect: agent?.data_to_collect,
        external_capabilities: initialExternalCapabilities,
        knowledge_ids: agent?.knowledge_ids,
        knowledge_name: knowledge?.name,
        knowledge_language: knowledge?.language,
        // An agent without a knowledge base (or whose one can't be loaded)
        // opens on "No knowledge base"; otherwise on editing the attached one.
        knowledge_mode: knowledge ? 'create' : 'none',
        webscrap_enabled: !!knowledge?.webscrap?.enabled,
        webscrap_frequency: knowledge?.webscrap?.frequency ?? 'weekly',
        webscrap_source_url: webscrapSource.url,
        webscrap_use_sitemap: webscrapSource.useSitemap,
        webscrap_max_depth: webscrapSource.maxDepth,
        webscrap_max_pages:
          knowledge?.webscrap?.max_pages ?? WEBSCRAP_DEFAULT_MAX_PAGES,
        webscrap_pages_per_pdf:
          knowledge?.webscrap?.pages_per_pdf ?? WEBSCRAP_DEFAULT_PAGES_PER_PDF,
        webscrap_links: parseWebscrapLinks(knowledge?.webscrap?.links),
        webscrap_exclude_url_patterns: parseExcludeUrlPatterns(
          knowledge?.webscrap?.exclude_url_patterns
        ),
        webscrap_scrape_images: !!knowledge?.webscrap?.scrape_images,
        webscrap_digests: parseDigests(knowledge?.webscrap?.digests),
        opening_sentence: agent?.opening_sentence,
        role_id: agent?.role_id,
        status: agent?.status,
        // Keep 'employee_directory' (a string, not an array) too: the backend
        // replaces external_capabilities wholesale on update, so dropping it
        // here deletes the transfer when the user saves without ever
        // visiting the Capabilities step.
        transfer_calls: agent?.external_capabilities?.transfer_calls ?? [],
        voice: agent?.voice,
        analytics_language: agent?.analytics_language ?? 'english',
        auto_fix: agent?.auto_fix ?? false,
        time_zone: agent?.time_zone ?? '',
        working_hours: agent?.working_hours ?? undefined,
        holidays: agent?.holidays ?? [],
        feedback_sampling_rate:
          agent?.feedback_sampling_rate != null
            ? agent.feedback_sampling_rate * 100
            : undefined,
        unknown_category_email:
          agent?.call_categories?.find(c => c.name === 'Unknown')?.email ??
          companyDetails?.email ??
          '',
        call_categories: (() => {
          const filtered = agent?.call_categories?.filter(
            c => c.name !== 'Unknown'
          );
          return filtered?.length
            ? filtered
            : [{ name: '', description: '', email: '' }];
        })(),
        access: normalizeResourceAccess(agent?.access),
      });
    }
  }, [agent, knowledge, workflow, methods, isBookingCapabilityEnabled]);

  const [isPlaying, setIsPlaying] = useState(false);
  const navigate = useNavigate();

  const handlePlayDemo = () => {
    setIsPlaying(!isPlaying);
  };

  const allSteps = [
    <AgentBehaviorStep
      key={0}
      label={agent ? t('Edit Agent Behavior') : t('Agent Behavior')}
      stepLabel={t('Step {{current}} of {{total}}', {
        current: 1,
        total: agent ? 4 : 5,
      })}
      description={t('You can pause the agent at any time after creation.')}
      agentVoice={agent && agent?.voice}
      showAccessFields={canEditAgentAccess}
      ownerUserId={agent?.user_id ?? currentUser?.id}
      isCreateMode={workflow === WorkflowType.CREATE}
    />,
    <AgentKnowledgeStep
      allowExistingKnowledgeSelection
      hasAttachedKnowledge={!!knowledge}
      currentUserId={currentUser?.id}
      pendingDeletions={pendingDeletions}
      deleteKnowledgeFileCallback={markKnowledgeFileForDeletion}
      unmarkKnowledgeFileForDeletion={unmarkKnowledgeFileForDeletion}
      downloadKnowledgeFileCallback={handleDownloadKnowledgeFile}
      description={t(
        'Upload here some files from which your agent will be based to answer user questions in a call. Do not forget that those information has to be public because they will be shared with your users.'
      )}
      key={1}
      knowledgeFiles={knowledgeFiles}
      knowledgeFilesData={knowledgeFilesData}
      label={knowledge ? t('Edit Agent Knowledge') : t('Agent Knowledge')}
      setKnowledgeFilesData={setKnowledgeFilesData}
      stepLabel={t('Step {{current}} of {{total}}', {
        current: 2,
        total: knowledge ? 4 : 5,
      })}
      setIsKnowledgeFilesLoading={setIsKnowledgeFilesLoading}
      onFieldChange={(field: string, value: string) => {
        // Track field changes for analytics
        track(AnalyticsEvents.KNOWLEDGE_FORM_FIELD_UPDATED, { field, value });
      }}
      isKnowledgeFilesLoading={isKnowledgeFilesLoading}
      showWebscrapConfig
      knowledgeId={knowledge?.id}
    />,
    <AgentCapabilitiesStep
      key={2}
      label={agent ? t('Edit Agent Capabilities') : t('Agent Capabilities')}
      stepLabel={t('Step {{current}} of {{total}}', {
        current: 3,
        total: agent ? 4 : 5,
      })}
      description={t(
        'Configure which actions your agent will be able to execute.'
      )}
      usedAgentPhoneNumbers={usedCustomPhoneNumbers}
      agentId={agent?.id || placeholderAgentId} // Use placeholder ID during creation
      workflow={workflow}
      agent={agent}
    />,
    <AgentAnalyticsSettingsStep
      key={3}
      agentType="call"
      fieldArrayName="call_categories"
      unknownCategoryEmailFieldName="unknown_category_email"
      label={agent ? t('Edit Agent Analytics') : t('Analytics Settings')}
      stepLabel={t('Step {{current}} of {{total}}', {
        current: 4,
        total: agent ? 4 : 5,
      })}
      description={t(
        'Configure how your agent categorizes and analyzes call conversations. Note that an email containing the conversation details will be sent to the email you entered each time a conversation has been given a category.'
      )}
    />,
    <AgentLineStep
      description={t('You can pause the agent at any time after creation.')}
      key={4}
      stepLabel={t('Step {{current}} of {{total}}', { current: 5, total: 5 })}
    />,
  ];
  // Hide the Agent Line step on edit workflow
  const steps =
    workflow === WorkflowType.EDIT
      ? allSteps.slice(0, allSteps.length - 1)
      : allSteps;

  const workflowSidebarSteps = [
    {
      title: t('Agent Behavior'),
      description: t('Configure identity, voice, and introduction.'),
    },
    {
      title: t('Agent Knowledge'),
      description: t('Attach your files and knowledge base content.'),
    },
    {
      title: t('Agent Capabilities'),
      description: t('Set transfers, appointments, and external actions.'),
    },
    {
      title: t('Analytics Settings'),
      description: t('Define categories and reporting behavior.'),
    },
    ...(workflow === WorkflowType.CREATE
      ? [
          {
            title: t('Agent Line'),
            description: isPartnerIframe
              ? t('Set a backup number. SIP routing is assigned in MixVoIP.')
              : t('Pick your agent phone line and routing setup.'),
          },
        ]
      : []),
  ];

  // The subset (and order) of steps actually shown for this workflow —
  // Edit omits the Line step, which is always last in stepEnumOrder.
  const applicableStepOrder = stepEnumOrder.slice(
    0,
    workflowSidebarSteps.length
  );

  // Live, non-blocking per-step completeness for the sidebar warning dot.
  // Derived from watched form values (not RHF's registered-field error
  // state) so it works even for steps the user hasn't visited yet — see
  // docs/specs/agent-form-click-to-jump-navigation.md ("Technical Risk").
  const watchedValues = methods.watch();
  const incompleteSteps = applicableStepOrder.map(
    stepEnum =>
      !isStepComplete(
        stepEnum,
        watchedValues as AgentFormData,
        lineCompletenessOptions
      )
  );

  // Next/sidebar navigation is intentionally non-validating — nothing
  // blocks moving between steps; only Save/Create validates (see the
  // Save-time full-validation gate further down).
  const goToNextStep = () => {
    persistStepDataToSessionStorage(currentStep, methods.getValues());
    goToStep(currentStep + 1, 'next');
  };

  const jumpToStep = (stepIndex: number) => {
    persistStepDataToSessionStorage(currentStep, methods.getValues());
    goToStep(stepIndex, 'jump');
  };

  // Shared Save-time full-validation gate — see
  // docs/specs/agent-form-click-to-jump-navigation.md. Jumps to the first
  // incomplete step and, once it has mounted, triggers RHF validation, the
  // call_categories pairing check, and a toast naming the step.
  const { jumpToIncompleteStep, onInvalidSubmit } = useStepValidationGate<
    AgentCreationStep,
    AgentFormData
  >({
    currentStep,
    goToStep,
    stepEnumOrder,
    applicableStepOrder,
    methods,
    workflowSidebarSteps,
    firstIncompleteStep: (values, stepOrder) =>
      firstIncompleteStep(values, stepOrder, lineCompletenessOptions),
    categoryPairing: {
      step: AgentCreationStep.ANALYTICS,
      fieldArrayName: 'call_categories',
    },
  });

  const onSubmit = async (data: AgentFormData) => {
    // Save-time full-validation gate — the only remaining validation gate
    // (see docs/specs/agent-form-click-to-jump-navigation.md). Jump to the
    // first incomplete step and abort; the effect above surfaces field
    // errors and a toast once that step has mounted.
    const incompleteStep = firstIncompleteStep(
      data,
      applicableStepOrder,
      lineCompletenessOptions
    );
    if (incompleteStep !== undefined) {
      jumpToIncompleteStep(incompleteStep);
      return;
    }

    // By this point the Save-time full-validation gate (see
    // firstIncompleteStep/isAnalyticsCategoriesComplete) has already confirmed
    // call_categories pairing is complete, so this step only needs to
    // shape the payload: strip empty rows and prepend the Unknown
    // catch-all category.
    const categories = data.call_categories ?? [];
    const nonEmpty = categories.filter(
      cat => cat.name?.trim() || cat.description?.trim()
    );
    data.call_categories = [
      {
        name: 'Unknown',
        description: 'Unknown',
        email: data.unknown_category_email || '',
      },
      ...nonEmpty,
    ];

    if (currentStep === steps.length - 1) {
      setIsProcessing(true);

      if (workflow === WorkflowType.CREATE) {
        await submitCreateAgent(data, {
          methods,
          skipPhoneLineRequirement: isPartnerIframe,
          isBookingCapabilityEnabled,
          currentUser,
          companyDetails,
          knowledgeFilesData,
          executeStagedDeletions,
          clearKnowledgeFilesData,
          queryClient,
          track,
          displayMessage,
          t,
          agentsCount,
          setIsProcessing,
          setIsKnowledgeCreated,
          setIsAgentCreated,
          setIsFilesUploaded,
          setAgentsCount,
          setNewAgent,
        });
      }

      if (workflow === WorkflowType.EDIT) {
        await submitUpdateAgent(data, {
          methods,
          isBookingCapabilityEnabled,
          companyDetails,
          canEditAgentAccess,
          agent,
          currentUser,
          knowledge,
          knowledgeFilesData,
          executeStagedDeletions,
          clearKnowledgeFilesData,
          track,
          displayMessage,
          t,
          setIsProcessing,
          setIsKnowledgeCreated,
          setIsAgentCreated,
          setIsFilesUploaded,
        });
      }
    }
  };

  const navigateToAgents = async () => {
    if (
      isFormDirty &&
      !isAgentCreated &&
      !isKnowledgeCreated &&
      !isFilesUploaded
    ) {
      setShowLeaveConfirmation(true);
    } else {
      setShowLeaveConfirmation(false);
      clearSessionStorage();
      clearIndexDBFiles();

      // Refetch agents cache to update list
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['agents'] }),
        queryClient.refetchQueries({ queryKey: ['knowledges'] }),
        queryClient.refetchQueries({
          queryKey: ['knowledges-with-attachment'],
        }),
      ]);

      navigate('/agents');
    }
  };

  const handleConfirmLeave = async () => {
    setShowLeaveConfirmation(false);
    clearSessionStorage();
    clearIndexDBFiles();

    // Refetch agents cache to update list
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['agents'] }),
      queryClient.refetchQueries({ queryKey: ['knowledges'] }),
      queryClient.refetchQueries({
        queryKey: ['knowledges-with-attachment'],
      }),
    ]);

    navigate('/agents');
  };

  const navigateToPricing = () => {
    track(AnalyticsEvents.CHOOSE_PLAN_BUTTON_CLICKED);
    setShowLeaveConfirmation(false);
    clearSessionStorage();
    clearIndexDBFiles();
    navigate('/pricing');
  };

  useEffect(() => {
    if (
      workflow === WorkflowType.CREATE &&
      isAgentCreated &&
      isKnowledgeCreated &&
      isFilesUploaded
    ) {
      setIsProcessing(false);
      setIsAgentCreationCompleted(true);
      // Update calendar auth state if calendar was connected prior to agent creation
      if (newAgent?.id) {
        const updateCalendarAuthState = async () => {
          const placeholderAgentId =
            sessionStorage.getItem('placeholderAgentId');
          if (placeholderAgentId && newAgent.id) {
            // Check if there was a calendar connection for the placeholder agent
            const { isCalendarConnected } = await import(
              '../../../services/calendarService'
            );
            if (isCalendarConnected(placeholderAgentId)) {
              // Update the calendar auth state to use the real agent ID
              setCalendarAuthState(newAgent.id, true);
              // Clear the placeholder agent's calendar connection
              const { clearCalendarAuthState } = await import(
                '../../../services/calendarService'
              );
              clearCalendarAuthState(placeholderAgentId);
            }
          }
        };

        updateCalendarAuthState();

        clearSessionStorage();
        clearIndexDBFiles();
      }
    }

    if (
      workflow === WorkflowType.EDIT &&
      isAgentCreated &&
      isKnowledgeCreated &&
      isFilesUploaded
    ) {
      setIsProcessing(false);
      navigateToAgents();
    }
  }, [isAgentCreated, isKnowledgeCreated, isFilesUploaded]);

  useEffect(() => {
    if (
      !isEmbed ||
      workflow !== WorkflowType.CREATE ||
      !isAgentCreationCompleted ||
      !newAgent?.id ||
      agentCreatedNotified.current
    ) {
      return;
    }

    agentCreatedNotified.current = true;
    notifyAgentCreated({
      agentType: 'call',
      agentId: newAgent.id,
      agentName: newAgent.name,
    });
  }, [isEmbed, workflow, isAgentCreationCompleted, newAgent]);

  useEffect(() => {
    const fetchAgents = async () => {
      // Get all phone numbers attached to existing company agents
      const response: ResponseObject = await getCompanyAgents();
      const agents = response?.message;

      if (!agents || !Array.isArray(agents)) {
        setUsedCustomPhoneNumbers([]);
        return;
      }

      const usedCustomPhoneNumbersList = getUsedCustomPhoneNumbers(agents);
      const usedSystemPhoneNumbersList = getUsedSystemPhoneNumbers(agents);
      setUsedCustomPhoneNumbers([
        ...usedCustomPhoneNumbersList,
        ...usedSystemPhoneNumbersList,
      ]);
    };

    if (currentUser) {
      fetchAgents();
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentStep === 0) {
      workflow === WorkflowType.CREATE
        ? track(AnalyticsEvents.AGENT_CREATION_STARTED)
        : track(AnalyticsEvents.AGENT_EDIT_STARTED);
    }

    return () => {
      if (!isAgentCreated) {
        workflow === WorkflowType.CREATE
          ? track(AnalyticsEvents.AGENT_CREATION_ABANDONED, {
              step: currentStepEnum,
            })
          : track(AnalyticsEvents.AGENT_EDIT_ABANDONED);
      }
    };
  }, [currentStep, currentStepEnum, workflow, isAgentCreated, track]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (
        isFormDirty &&
        !sessionStorage.getItem('calendar_oauth_in_progress')
      ) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isFormDirty]);

  useEffect(() => {
    if (isAgentCreated) {
      workflow === WorkflowType.CREATE
        ? track(AnalyticsEvents.AGENT_CREATION_COMPLETED)
        : track(AnalyticsEvents.AGENT_EDIT_COMPLETED);
    }
  }, [isAgentCreated]);

  if (workflow === WorkflowType.CREATE && isAgentCreationCompleted) {
    return (
      <AgentCreationSuccessContainer>
        <Stack
          spacing={2.5}
          textAlign="center"
          alignItems="center"
          justifyContent="center"
          sx={{ flex: 1, minWidth: 0, maxWidth: 560, margin: '0 auto' }}
        >
          <AgentCreationSuccessActions
            congratsMessage={
              newAgent?.name
                ? t("You've created the agent {{name}}!", {
                    name: newAgent?.name,
                  })
                : t("You've created a new agent!")
            }
            onGoToAgents={navigateToAgents}
            showDone={isEmbed}
            onDone={notifyEmbedClose}
            showChooseAPlan={shouldShowUpgradeCta(companyDetails, subscription)}
            onChooseAPlan={navigateToPricing}
          />
        </Stack>
        <Stack sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
          <AgentProfileAvatar
            aiAgentImage={newAgent?.avatar}
            aiAgentVoice={
              newAgent?.voice === 'female' ? femaleAudioFile : maleAudioFile
            }
            agentId={newAgent?.id}
            agentName={newAgent?.name || 'Smith'}
            agentStatus={newAgent?.status}
            hideAgentName
            isPlaying={isPlaying}
            playAgentVoice={handlePlayDemo}
            width="360px"
            voice={newAgent?.voice}
            showTestButton={true}
          />
        </Stack>
      </AgentCreationSuccessContainer>
    );
  }
  // Agent knowledge and behaviour are being created, and
  // files are being uploaded UI.
  if (workflow === WorkflowType.CREATE && isProcessing) {
    return (
      <AgentCreationProcessingScreen
        title={t('Call Agent Creation in Progress')}
        isKnowledgeCreated={isKnowledgeCreated}
        isAgentCreated={isAgentCreated}
        agentCreatedLabel={t('Call Agent Created')}
        creatingAgentLabel={t('Creating Call Agent...')}
        isFilesUploaded={isFilesUploaded}
        withKnowledgeBase={methods.getValues('knowledge_mode') !== 'none'}
      />
    );
  }

  if (workflow === WorkflowType.CREATE && isExistingAgentsLoading) {
    return null;
  }

  if (
    workflow === WorkflowType.CREATE &&
    shouldBlockNewCallAgent(companyDetails, existingAgentsCount)
  ) {
    const canManageBilling = Boolean(
      currentUser && canPerformAction(currentUser, PERMISSIONS.MANAGE_BILLING)
    );
    return (
      <FreePlanLimitModal
        onViewPlans={canManageBilling ? () => navigate('/pricing') : undefined}
        onBackToAgents={() => navigate('/agents')}
      >
        {!canManageBilling && (
          <Text type="body2">
            {t('Contact your company admin about this.')}
          </Text>
        )}
      </FreePlanLimitModal>
    );
  }
  // Filling out the required fields.
  return (
    <>
      <FormProvider {...methods}>
        <StyledForm
          onSubmit={methods.handleSubmit(onSubmit, onInvalidSubmit)}
          maxWidth="100%"
          margin="0"
          marginTop="0"
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2.5}
            sx={{
              width: '100%',
              maxWidth: 1280,
              mx: 'auto',
              p: { xs: 1.5, md: 2.5 },
              borderRadius: 4,
              bgcolor: '#F6F8FC',
              border: '1px solid rgba(15, 23, 42, 0.06)',
            }}
          >
            <Stack
              spacing={2.5}
              sx={{
                width: { xs: '100%', md: 280 },
                flexShrink: 0,
                justifyContent: 'space-between',
              }}
            >
              <WorkflowSidebar
                title={t('Create your Agent')}
                steps={workflowSidebarSteps}
                currentStep={currentStep}
                incompleteSteps={incompleteSteps}
                onStepClick={jumpToStep}
              />
              <Box
                sx={{
                  p: 2.5,
                }}
              >
                <BillingSupportLinks />
              </Box>
            </Stack>
            <Stack sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
              <StyledFormStep
                width={
                  currentStep === 2 || currentStep === 3
                    ? '728px' /* for capabilities step only */
                    : '500px'
                }
                maxWidth={
                  currentStep === 2 || currentStep === 3
                    ? '728px' /* for capabilities step only */
                    : '500px'
                }
                sx={{
                  bgcolor: '#FFFFFF',
                  borderRadius: 3,
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
                  p: { xs: 2, md: 3 },
                }}
              >
                {steps[currentStep]}
              </StyledFormStep>
            </Stack>
          </Stack>
          <Box sx={{ maxWidth: 1280, width: '100%', mx: 'auto' }}>
            <WorkflowFooter
              currentStep={currentStep}
              isKnowledgeFilesLoading={isKnowledgeFilesLoading}
              isProcessing={isProcessing}
              navigateToAgents={navigateToAgents}
              previousStep={goToPreviousStep}
              steps={steps}
              workflow={workflow}
              onNext={goToNextStep}
            />
          </Box>
        </StyledForm>
      </FormProvider>
      <Modal
        mainPromptText={t(
          'Are you sure you want to leave? Your changes will be lost.'
        )}
        description={t(
          'You have unsaved changes. If you leave now, your changes will not be saved.'
        )}
        confirmText={t('Yes, leave')}
        denyText={t('No, stay')}
        modalShouldBeOpen={showLeaveConfirmation}
        setChangeStatusModalOpen={setShowLeaveConfirmation}
        callback={handleConfirmLeave}
        showButton={false}
      />
    </>
  );
};

export default AgentForm;
